import React, { useReducer, useState } from "react";
import { Modal, Form, Row, Col, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { ExclamationCircleFill } from "react-bootstrap-icons";
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { AuthConsumer } from "../lib/contexts/AuthContext";
import qs from "qs";

const ResetPassword = (props) => {
	const initialState = {
		loading: false,
		error: [],
		password: {
			value: "",
			required: true,
			touched: false,
			invalid: true,
			message: "",
		},
		confirmPassword: {
			value: "",
			required: true,
			touched: false,
			invalid: true,
			message: "",
		},
		isAlphaNumbericError: false,
		isCasingError: false,
		isLengthError: false,
		isSpecialCharacterError: false,
		isUniqueCharactersError: false,
	};

	const reducer = (state, action) => {
		switch (action.type) {
			case "idBlurred":
				return {
					...state,
					[action.payload]: {
						...state[action.payload],
						touched: true,
					},
				};
			case "idChanged":
				return {
					...state,
					[action.payload.id]: {
						...state[action.payload],
						value: action.payload.value,
					},
				};
			case "invalidateFields":
				return {
					...state,
					[action.payload.id]: {
						...state[action.payload.id],
						invalid: action.payload.invalid,
						message: action.payload.message,
					},
				};
			case "clearErrors":
				return {
					...state,
					[action.payload]: {
						...state[action.payload],
						invalid: false,
						message: "",
					},
				};
			case "loading":
				return {
					...state,
					loading: action.payload,
				};
			case "error":
				return {
					...state,
					error: action.payload,
					loading: false,
				};
			case "password":
				return {
					...state,
					password: action.payload,
				};
			case "confirmPassword":
				return {
					...state,
					password: action.payload,
				};
			default:
				throw new Error();
		}
	};

	const [passwordChanged, setPasswordChanged] = useState(false);
	const [saving, setSaving] = useState(false);
	const [resetPasswordState, dispatch] = useReducer(reducer, initialState);
	const location = useLocation();
	const navigate = useNavigate();

	const handleBlur = (event) => {
		const { id } = event.target;
		dispatch({ type: "idBlurred", payload: id });
	};

	const handleChange = (callback) => (event) => {
		const { id, value } = event.target;
		dispatch({ type: "idChanged", payload: { id, value, touched: true } });

		const field = resetPasswordState[id];
		if (field.required) requireField(id);
		if (typeof callback === "function") {
			callback();
		}
	};

	const shouldMarkError = (id) => {
		const field = resetPasswordState[id];
		const result = field.invalid ? field.touched : false;
		return result;
	};

	const requireField = (id) => {
		var invalid = false;
		var message = "";
		var field = resetPasswordState[id];
		if (field.required) {
			if (field.value === "") {
				invalid = true;
				message = "Required. ";
			}
		}
		invalidateField(id, invalid, message);
	};

	const invalidateField = (id, invalid, message) => {
		dispatch({ type: "invalidateFields", payload: { id, invalid, message } });
	};

	const formIsValid = () => {
		var invalidFields = Object.keys(resetPasswordState).filter(
			(field) =>
				resetPasswordState[field].hasOwnProperty("invalid") &&
				resetPasswordState[field].invalid === true
		);
		return invalidFields.length === 0;
	};

	const checkPasswordsMatch = () => {
		const { password, confirmPassword } = resetPasswordState;

		clearErrors();
		requireField("password");
		requireField("confirmPassword");
		if (
			password.touched &&
			confirmPassword.touched &&
			password.value !== confirmPassword.value
		) {
			invalidateField("password", true, "Passwords do not match");
			invalidateField("confirmPassword", true, "Passwords do not match");
		}
	};

	const clearErrors = (id) => {
		dispatch({ type: "clearErrors", payload: id });
	};

	const isAlphaNumbericError = () => {
		return (
			(resetPasswordState.error.includes("PasswordRequiresDigit") ||
				(resetPasswordState.error.includes("PasswordRequiresLower") &&
				resetPasswordState.error.includes("PasswordRequiresUpper"))) &&
			"text-danger"
		);
	};

	const isSpecialCharacterError = () => {
		return (
			resetPasswordState.error.includes("PasswordRequiresNonAlphanumeric") && 'text-danger'
		);
	};

	const isCasingError = () => {
		return (
			(resetPasswordState.error.includes("PasswordRequiresUpper") ||
			resetPasswordState.error.includes("PasswordRequiresLower")) && 'text-danger'
		)
	};

	const isUniqueCharactersError = () => {
		return (
			resetPasswordState.error.includes("PasswordRequiresUniqueChars") && 'text-danger'
		);
	};

	const isLengthError = () => {
		return (
			resetPasswordState.error.includes("PasswordTooShort") && 'text-danger'
		);
	};

	async function submit(event) {
		event.preventDefault();
		setSaving(true);
		const { password } = resetPasswordState;
		const { userId, code } = qs.parse(location.search, {
			encode: false,
			ignoreQueryPrefix: true,
		});

		if (formIsValid()) {
			dispatch({ type: "loading", payload: true });
			let passwordReset = {
				userId: userId,
				resetCode: code,
				password: password.value,
			};

			await props.api
				.postPasswordReset("api/auth/password-reset", { ...passwordReset })
				.then((response) => {
					if (response.status === 200) {
						setPasswordChanged(true);
						setTimeout(handleClose, 7000);
					}
					throw new Error("Password Reset failed. Please try again.");
				})
				.catch((error) => {
					dispatch({ type: "error", payload: error.data });
				});
		}
		setSaving(false);
	}

	const handleClose = () => {
		navigate('/login');
	};

	const { error, password, confirmPassword } = resetPasswordState;

	return (
		<>
		{passwordChanged ? (
			<Modal show={passwordChanged} onHide={handleClose}>
			<Modal.Header closeButton>
				<Modal.Title>Password Successfully Changed</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Row>
					<Col>
						Your password has been successfully changed. You will now be redirected to the <a href="/login">login</a> page. 
						Thank you for using the ClearPoint Portal!
					</Col>
				</Row>
			</Modal.Body>
		</Modal>
		) :(
		<AuthConsumer>
			{(context) => (
				<div className="sm-panel">
					<Form onSubmit={() => submit(context)}>
						<div className="row">
							{(error.length > 0 && error.includes("Password reset failed. Please contact support")) && (
								<div className="form-group row">
									<div className="col-sm-12 mb-3">
										<div className="alert alert-danger m-auto" role="alert">
											<span className="me-1" aria-hidden="true">
												{<ExclamationCircleFill />}
											</span>
											<span className="sr-only">Error: </span>
											{' Password reset failed. Please contact support.'}
										</div>
									</div>
								</div>
							)}
							<div className="col-sm-12">
								<div className="header section">Reset Password</div>
								<div className="section muted-bold">
									Please reset your password.
								</div>
							</div>
						</div>
						<div className="row">
							<div className="col-sm-12">
								Your password must:
								<ul>
									<li className={isAlphaNumbericError()}>
										contain a mix of alphabetical characters and numbers
									</li>
									<li className={isSpecialCharacterError()}>
										contain at least one special character
									</li>
									<li className={isCasingError()}>
										contain a mix of upper and lower case letters
									</li>
									<li className={isUniqueCharactersError()}>
										contain at least 6 unique characters
									</li>
									<li className={isLengthError()}>
										be at least 8 characters long
									</li>
								</ul>
							</div>
						</div>
						<Row>
							<Col sm={12}>
								<Form.Group>
									<Form.Control
										type="password"
										className={shouldMarkError("password") ? " error" : null}
										id="password"
										name="password"
										value={password.value}
										onBlur={handleBlur}
										placeholder="Password"
										onChange={handleChange(checkPasswordsMatch)}
									/>
								</Form.Group>
							</Col>
						</Row>
						<Row className="mt-3">
							<Col sm={12}>
								<Form.Group>
									<Form.Control
										type="password"
										className={
											shouldMarkError("confirmPassword") ? " error" : null
										}
										id="confirmPassword"
										name="confirmPassword"
										placeholder="Confirm Password"
										value={confirmPassword.value}
										onBlur={handleBlur}
										onChange={handleChange(checkPasswordsMatch)}
									/>
								</Form.Group>
							</Col>
						</Row>
						<Row className="section mt-3">
							<Col sm={12}>
								<Form.Group>
									{saving
									? <Spinner animation="border" role="status" variant='primary'>
									<span className="visually-hidden">Loading...</span>
									</Spinner>
									:<button
										disabled={!formIsValid()}
										onClick={submit}
										className="btn btn-primary">
										Submit
									</button>
									}
								</Form.Group>
							</Col>
						</Row>
					</Form>
				</div>
			)}
		</AuthConsumer>)}</>
	);
};

export default WithApiAccess(ResetPassword);
